import React, { useCallback, useEffect, useState } from "react";
import { BellRing, BellOff, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { formatApiError } from "../lib/api";
import { getPushState, enablePush, disablePush, sendTestPush, isIOS, isStandalone } from "../lib/push";

const IosHint = () => (
  <div className="text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-1" data-testid="push-ios-hint">
    <div className="font-semibold">Su iPhone le notifiche funzionano solo dall'app installata:</div>
    <div>1. Apri il sito in Safari · 2. Tocca Condividi · 3. "Aggiungi alla schermata Home" · 4. Apri Striano NCC dall'icona e attiva qui.</div>
  </div>
);

export const PushNotificationsCard = ({ compact = false }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => getPushState().then(setSt).catch(() => setSt({ supported: false })), []);
  useEffect(() => { refresh(); }, [refresh]);

  const needsInstall = isIOS() && !isStandalone();
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); }
    catch (e) { toast.error(e?.response ? formatApiError(e) : e.message); }
    finally { setBusy(false); refresh(); }
  };
  const testMsg = (r) => (r?.sent ? `Notifica di prova inviata a ${r.sent} dispositivo/i` : "Nessun dispositivo attivo ha ricevuto la prova");

  if (!st) return null;
  const on = st.subscribed;
  return (
    <div className={compact ? "space-y-2" : "rounded-xl border border-amber-500/20 bg-slate-950/60 p-5 space-y-4"} data-testid="push-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center"><Smartphone size={18} className="text-amber-400" /></div>
          <div>
            <div className="text-white font-semibold">Notifiche push su questo dispositivo</div>
            <div className="text-xs text-slate-400" data-testid="push-status-text">
              {!st.supported ? "Non supportate da questo browser" : on ? `Attive · ${st.devices} dispositivo/i registrati` : st.permission === "denied" ? "Bloccate dalle impostazioni del dispositivo" : "Disattivate"}
            </div>
          </div>
        </div>
        {st.supported && !needsInstall && (
          <div className="flex gap-2">
            {on ? (
              <>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(sendTestPush, testMsg)} className="border-amber-500/40 bg-transparent text-amber-300" data-testid="push-test-btn"><Send size={14} className="mr-1" />Invia prova</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(disablePush, "Notifiche push disattivate")} className="border-slate-700 bg-transparent text-slate-300" data-testid="push-disable-btn"><BellOff size={14} className="mr-1" />Disattiva</Button>
              </>
            ) : (
              <Button size="sm" disabled={busy || st.permission === "denied" || !st.configured} onClick={() => run(enablePush, "Notifiche push attivate")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="push-enable-btn"><BellRing size={14} className="mr-1" />Attiva notifiche</Button>
            )}
          </div>
        )}
      </div>
      {needsInstall && <IosHint />}
      {!compact && <div className="text-xs text-slate-500">Riceverai avvisi per servizi assegnati, modificati e cancellati. Le notifiche non contengono dati di clienti né importi.</div>}
    </div>
  );
};

export default PushNotificationsCard;
